import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Button, Modal, Form, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import config from '../config/config';
import { useResto } from '../contexts/RestoContext';
import { useMode } from '../contexts/ModeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ucfirst } from '../utils/utils';
import MediaList from './MediaList';

function SupplierItem({
    supplier,
    handleDelete,
    handleRankingUpdate,
    handleEdit,
    isFirst,
    isLast
}) {

    const { t } = useTranslation();
    const { isModeAdmin } = useMode();
    const { restoData } = useResto();
    const { activeLanguage } = useLanguage();

    const availableLanguages = restoData?.availableLanguages || ['fr'];

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const [editedSupplier, setEditedSupplier] = useState({
        ...supplier
    });

    const handleRanking = (direction) => {
        handleRankingUpdate(direction, supplier.id);
    };

    const handleConfirmDelete = () => {
        handleDelete(supplier.id);
        setShowConfirmation(false);
    };

    const handleEditClick = () => {
        setEditedSupplier({ ...supplier });
        setShowEditModal(true);
    };

    const handleEditSubmit = () => {
        handleEdit(supplier.id, editedSupplier);
        setShowEditModal(false);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditedSupplier(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const url = supplier?.url || '';
    const truncatedLinkText =
        url.length > 35 ? `${url.substring(0, 32)}...` : url;

    return (
        <div className="kubuni-frame" style={{ paddingBottom: '20px' }}>

            {/* ================= ADMIN MODE ================= */}
            {isModeAdmin && (
                <>
                    <div style={{ fontWeight: 'bold' }}>
                        {supplier?.label}
                    </div>

                    {url && (
                        <div style={{ fontWeight: 'bold', marginBottom: '20px' }}>
                            <a href={url} style={{ color: 'black' }} target="_blank" rel="noreferrer">
                                {truncatedLinkText}
                            </a>
                        </div>
                    )}

                    {availableLanguages.map(language => (
                        <div key={language}>
                            <div>
                                <Image
                                    src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                    className="flag-label"
                                    alt={language}
                                />
                                {' '}
                                {supplier?.[`description${ucfirst(language)}`]}
                            </div>
                        </div>
                    ))}

                    <div style={{ marginTop: '10px' }}>
                        <Button size="sm" onClick={handleEditClick}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>

                        {!isFirst && (
                            <Button
                                size="sm"
                                onClick={() => handleRanking('up')}
                                style={{ marginLeft: '5px' }}
                            >
                                <FontAwesomeIcon icon={faAngleUp} />
                            </Button>
                        )}

                        {!isLast && (
                            <Button
                                size="sm"
                                onClick={() => handleRanking('down')}
                                style={{ marginLeft: '5px' }}
                            >
                                <FontAwesomeIcon icon={faAngleDown} />
                            </Button>
                        )}

                        <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setShowConfirmation(true)}
                            style={{ marginLeft: '5px' }}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>

                    <MediaList
                        mediaKey={'SUPPLIER' + supplier?.id}
                        maxItems={10}
                    />

                    {/* ===== DELETE MODAL ===== */}
                    <Modal
                        show={showConfirmation}
                        onHide={() => setShowConfirmation(false)}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>{t('CONFIRM_DELETE')}</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <p>{t('CONFIRM_DELETE_MESSAGE')}</p>
                        </Modal.Body>

                        <Modal.Footer className="justify-content-center">
                            <Button
                                variant="secondary"
                                onClick={() => setShowConfirmation(false)}
                            >
                                {t('CANCEL')}
                            </Button>

                            <Button
                                variant="danger"
                                onClick={handleConfirmDelete}
                            >
                                {t('CONFIRM')}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* ===== EDIT MODAL ===== */}
                    <Modal
                        show={showEditModal}
                        onHide={() => setShowEditModal(false)}
                        size="xl"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>{t('EDIT')}</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <Form>

                                <Form.Group className="mb-3">
                                    <Form.Label>Titre</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="label"
                                        value={editedSupplier?.label || ''}
                                        onChange={handleEditInputChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>URL</Form.Label>
                                    <Form.Control
                                        type="url"
                                        name="url"
                                        value={editedSupplier?.url || ''}
                                        onChange={handleEditInputChange}
                                    />
                                </Form.Group>

                                {availableLanguages.map(language => (
                                    <Form.Group
                                        key={language}
                                        className="mb-3"
                                    >
                                        <Form.Label>
                                            Description{' '}
                                            <Image
                                                src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                                className="flag-label"
                                                alt={language}
                                            />
                                        </Form.Label>

                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name={`description${ucfirst(language)}`}
                                            value={
                                                editedSupplier?.[
                                                    `description${ucfirst(language)}`
                                                ] || ''
                                            }
                                            onChange={handleEditInputChange}
                                        />
                                    </Form.Group>
                                ))}

                            </Form>
                        </Modal.Body>

                        <Modal.Footer className="justify-content-center">
                            <Button
                                variant="secondary"
                                onClick={() => setShowEditModal(false)}
                            >
                                {t('CANCEL')}
                            </Button>

                            <Button
                                variant="primary"
                                onClick={handleEditSubmit}
                            >
                                {t('CONFIRM')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )}

            {/* ================= USER MODE ================= */}
            {!isModeAdmin && (
                <div>

                    <div style={{ fontWeight: 'bold' }}>
                        {supplier?.label}
                    </div>

                    {url && (
                        <div style={{ fontWeight: 'bold' }}>
                            <a
                                href={url}
                                style={{ color: 'black' }}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {truncatedLinkText}
                            </a>
                        </div>
                    )}

                    <div>
                        {supplier?.[
                            `description${ucfirst(activeLanguage)}`
                        ]}
                    </div>

                    <MediaList
                        mediaKey={'SUPPLIER' + supplier?.id}
                        maxItems={10}
                    />

                </div>
            )}

        </div>
    );
}

export default SupplierItem;